import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Notification } from '../types';
import { notificationService } from '../services/notificationService';
import { useAuth } from './AuthContext';

interface NotificationContextType {
    notifications: Notification[];
    unreadCount: number;
    markAsRead: (id: string) => void;
    markAllAsRead: () => void;
    refreshNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { user } = useAuth();
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);

    const refreshNotifications = () => {
        if (user) {
            const userNotifications = notificationService.getNotifications(user.id || '');
            setNotifications(userNotifications);
            setUnreadCount(userNotifications.filter(n => !n.read).length);
        } else {
            setNotifications([]);
            setUnreadCount(0);
        }
    };

    useEffect(() => {
        refreshNotifications();
        // Poll for new notifications every 5 seconds
        const interval = setInterval(refreshNotifications, 5000);
        return () => clearInterval(interval);
    }, [user]);

    const markAsRead = (id: string) => {
        notificationService.markAsRead(id);
        refreshNotifications();
    };

    const markAllAsRead = () => {
        if (user) {
            notificationService.markAllAsRead(user.id || '');
            refreshNotifications();
        }
    };

    return (
        <NotificationContext.Provider value={{ notifications, unreadCount, markAsRead, markAllAsRead, refreshNotifications }}>
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (context === undefined) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
};
